import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Plus, Calendar, Download, FileText, RefreshCw, Building2,
  ArrowLeftRight, Sun, Moon, Sparkles
} from 'lucide-react';
import {
  PieChart as RePieChart, Pie, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmtNum } from '@/utils/currency';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const THEMES = {
  light: { key: 'light', label: 'Light', icon: Sun },
  dark: { key: 'dark', label: 'Dark', icon: Moon },
  glass: { key: 'glass', label: 'Glass Thermal', icon: Sparkles },
};

const THERMAL_GRADIENTS = [
  'from-rose-500 via-orange-500 to-amber-400',
  'from-violet-500 via-fuchsia-500 to-pink-400',
  'from-cyan-500 via-blue-500 to-indigo-400',
  'from-emerald-500 via-teal-400 to-cyan-400',
];

export default function Budget() {
  const { token } = useContext(AuthContext);
  const [theme, setTheme] = useState(() => localStorage.getItem('dashboard-theme') || 'light');
  const isGlass = theme === 'glass';
  const isDark = theme === 'dark' || theme === 'glass';

  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [financialInsights, setFinancialInsights] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [accountingSync, setAccountingSync] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    category_id: '', amount: '', period: 'monthly',
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchCategories(); fetchBudgets(); fetchAnalytics();
    fetchForecast(); fetchFinancialInsights(); fetchAccountingSync();
  }, [period]);

  // Convert USD amount to display currency
  const convertFromUSD = (usdAmount) => {
    if (!accountingSync?.exchange_rates || displayCurrency === 'USD') return usdAmount;
    const rate = accountingSync.exchange_rates[displayCurrency];
    return rate ? usdAmount * rate : usdAmount;
  };
  const currSym = displayCurrency === 'USD' ? '$' : displayCurrency + ' ';
  const availableCurrencies = accountingSync?.currency_totals ? ['USD', ...Object.keys(accountingSync.currency_totals).filter(c => c !== 'USD')] : ['USD'];

  useEffect(() => { localStorage.setItem('dashboard-theme', theme); }, [theme]);

  const fetchCategories = async () => {
    try { const r = await axios.get(`${API}/expense-categories`, { headers: { Authorization: `Bearer ${token}` } }); setCategories(r.data); } catch (e) { console.error('Failed to fetch categories'); }
  };
  const fetchBudgets = async () => {
    try { const r = await axios.get(`${API}/budgets?period=${period}`, { headers: { Authorization: `Bearer ${token}` } }); setBudgets(r.data); } catch (e) { toast.error('Failed to fetch budgets'); }
  };
  const fetchAnalytics = async () => {
    try { const r = await axios.get(`${API}/budgets/analytics/summary?period=${period}`, { headers: { Authorization: `Bearer ${token}` } }); setAnalytics(r.data); } catch (e) { console.error('Failed to fetch analytics'); }
  };
  const fetchForecast = async () => {
    try {
      const r = await axios.get(`${API}/budgets/forecast/ai-enhanced`, { headers: { Authorization: `Bearer ${token}` } });
      setForecast(r.data);
    } catch {
      try { const r = await axios.get(`${API}/budgets/forecast/next-month`, { headers: { Authorization: `Bearer ${token}` } }); setForecast(r.data); } catch { console.error('Failed to fetch forecast'); }
    }
  };
  const fetchFinancialInsights = async () => {
    try { const r = await axios.get(`${API}/budgets/financial-health-insights?period=${period}`, { headers: { Authorization: `Bearer ${token}` } }); setFinancialInsights(r.data); } catch { console.error('Failed to fetch financial insights'); }
  };
  const fetchAccountingSync = async () => {
    try { const r = await axios.get(`${API}/budgets/accounting-sync?period=${period}`, { headers: { Authorization: `Bearer ${token}` } }); setAccountingSync(r.data); } catch { console.error('Failed to fetch accounting sync'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/budgets`, { ...formData, amount: parseFloat(formData.amount), start_date: new Date(formData.start_date).toISOString() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Budget created successfully');
      setIsDialogOpen(false);
      fetchBudgets(); fetchAnalytics();
      setFormData({ category_id: '', amount: '', period: 'monthly', start_date: new Date().toISOString().split('T')[0] });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create budget'); }
  };

  const handleDelete = async (budgetId) => {
    if (!confirm('Are you sure you want to delete this budget?')) return;
    try { await axios.delete(`${API}/budgets/${budgetId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Budget deleted'); fetchBudgets(); fetchAnalytics(); } catch { toast.error('Failed to delete budget'); }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return isGlass ? 'text-emerald-400' : 'text-green-600';
    if (score >= 60) return isGlass ? 'text-amber-400' : 'text-yellow-600';
    return isGlass ? 'text-rose-400' : 'text-red-600';
  };

  const exportToCSV = () => {
    if (!budgets.length) return;
    const csv = [['Category', 'Budgeted', 'Actual', 'Remaining', 'Status'].join(','),
      ...budgets.map(b => [b.category_name, b.amount, b.actual_spent || 0, b.remaining || 0, b.status].join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `budget-report-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success('CSV exported');
  };

  const exportToPDF = () => {
    if (!budgets.length) return;
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString();
    doc.setFontSize(20); doc.setTextColor(30, 58, 138); doc.text('Budget Report', 14, 22);
    doc.setFontSize(10); doc.setTextColor(100); doc.text(`Generated: ${today} | Period: ${period}`, 14, 30);
    if (analytics) {
      doc.setFontSize(14); doc.setTextColor(0); doc.text('Summary', 14, 42);
      autoTable(doc, { startY: 46, head: [['Metric', 'Value']], body: [
        ['Total Budgeted', `$${fmtNum(analytics.total_budgeted || 0)}`],
        ['Total Spent', `$${fmtNum(analytics.total_spent || 0)}`],
        ['Remaining', `$${fmtNum(analytics.remaining || 0)}`],
        ['Financial Health', `${analytics.financial_health_score || 0}%`],
      ], theme: 'grid', headStyles: { fillColor: [30, 58, 138] }, styles: { fontSize: 10 } });
    }
    const tableY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : 80;
    doc.setFontSize(14); doc.setTextColor(0); doc.text('Budget Details', 14, tableY);
    autoTable(doc, { startY: tableY + 4, head: [['Category', 'Budgeted', 'Actual Spent', 'Remaining', '% Used', 'Status']],
      body: budgets.map(b => [b.category_name, `$${fmtNum(b.amount)}`, `$${fmtNum(b.actual_spent || 0)}`, `$${fmtNum(Math.abs(b.remaining || 0))}`, `${(b.percentage_used || 0).toFixed(1)}%`, b.status === 'over' ? 'Over Budget' : 'Under Budget']),
      theme: 'grid', headStyles: { fillColor: [30, 58, 138] }, styles: { fontSize: 9 }, columnStyles: { 4: { halign: 'right' }, 5: { halign: 'center' } } });
    if (accountingSync) {
      const syncY = doc.lastAutoTable.finalY + 12; doc.setFontSize(14); doc.text('Accounting Sync', 14, syncY);
      autoTable(doc, { startY: syncY + 4, head: [['Metric', 'Value']], body: [
        ['Total Available Balance', `$${fmtNum(accountingSync.total_available_balance || 0)}`],
        ['Total Budgeted', `$${fmtNum(accountingSync.total_budgeted || 0)}`],
        ['Expenses This Period', `$${fmtNum(accountingSync.total_expenses_this_period || 0)}`],
        ['Budget Remaining', `$${fmtNum(accountingSync.budget_remaining || 0)}`],
        ['Budget Utilization', `${accountingSync.budget_utilization || 0}%`],
      ], theme: 'grid', headStyles: { fillColor: [16, 185, 129] }, styles: { fontSize: 10 } });
    }
    if (forecast && forecast.forecasts) {
      const forecastY = doc.lastAutoTable.finalY + 12;
      if (forecastY < 250) {
        doc.setFontSize(14); doc.text('Next Month Forecast', 14, forecastY);
        autoTable(doc, { startY: forecastY + 4, head: [['Category', 'Predicted Amount', 'Confidence', 'Trend']],
          body: forecast.forecasts.map(f => [f.category_name, `$${fmtNum(f.predicted_amount || 0)}`, f.confidence, f.trend]),
          theme: 'grid', headStyles: { fillColor: [99, 102, 241] }, styles: { fontSize: 9 } });
      }
    }
    doc.save(`budget-report-${new Date().toISOString().split('T')[0]}.pdf`); toast.success('PDF exported');
  };

  // Theme tokens
  const pageBg = isGlass ? 'bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950'
    : theme === 'dark' ? 'bg-slate-950' : 'bg-slate-50';
  const card = isGlass ? 'bg-white/5 backdrop-blur-xl border border-white/10'
    : isDark ? 'bg-slate-900/80 border border-slate-700/50' : '';
  const txt = isDark ? 'text-white' : 'text-slate-900';
  const sub = isGlass ? 'text-white/60' : isDark ? 'text-slate-400' : 'text-slate-600';
  const switcherBg = isDark ? 'bg-white/10' : 'bg-slate-100';
  const chartTickColor = isDark ? '#94a3b8' : '#64748b';
  const chartGridColor = isGlass ? 'rgba(255,255,255,0.08)' : isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0';

  const summaryCards = analytics ? [
    { label: 'Total Budgeted', value: `$${fmtNum(analytics.total_budgeted || 0)}`, color: isGlass ? 'text-blue-400' : 'text-blue-600', thermal: THERMAL_GRADIENTS[0] },
    { label: 'Total Spent', value: `$${fmtNum(analytics.total_spent || 0)}`, color: txt, thermal: THERMAL_GRADIENTS[1] },
    { label: 'Remaining', value: `$${fmtNum(Math.abs(analytics.remaining || 0))}`, color: (analytics.remaining || 0) >= 0 ? (isGlass ? 'text-emerald-400' : 'text-green-600') : (isGlass ? 'text-rose-400' : 'text-red-600'), thermal: THERMAL_GRADIENTS[2] },
    { label: 'Financial Health', value: `${analytics.financial_health_score || 0}%`, color: getHealthColor(analytics.financial_health_score || 0), thermal: THERMAL_GRADIENTS[3] },
  ] : [];

  return (
    <Layout>
      <div className={`min-h-screen ${pageBg} transition-colors duration-500`}>
        <div className="p-6 space-y-6" data-testid="budget-dashboard">

          {/* Header */}
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div>
              <h1 className={`text-3xl font-bold ${txt}`}>Budget Planner</h1>
              <p className={sub}>Manage budgets and track spending</p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Button variant={isDark ? 'secondary' : 'outline'} onClick={exportToCSV}
                className={isGlass ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : ''}
                data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" /> CSV
              </Button>
              <Button variant={isDark ? 'secondary' : 'outline'} onClick={exportToPDF}
                className={isGlass ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : ''}
                data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className={isGlass ? 'bg-emerald-500/80 hover:bg-emerald-500 border-0' : ''}>
                    <Plus className="w-4 h-4 mr-2" /> Add Budget
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Budget</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Category *</Label>
                      <select value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        className="w-full border rounded-md px-3 py-2" required>
                        <option value="">Select category</option>
                        {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Budget Amount (USD) *</Label>
                      <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
                    </div>
                    <div>
                      <Label>Period *</Label>
                      <select value={formData.period} onChange={(e) => setFormData({ ...formData, period: e.target.value })} className="w-full border rounded-md px-3 py-2" required>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <Label>Start Date *</Label>
                      <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1">Create</Button>
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Theme Switcher */}
              <div className={`flex gap-1 p-1 rounded-lg ${switcherBg}`} data-testid="budget-theme-switcher">
                {Object.values(THEMES).map((th) => {
                  const ThIcon = th.icon;
                  return (
                    <button key={th.key} onClick={() => setTheme(th.key)}
                      className={`p-2 rounded-md transition-all ${
                        theme === th.key
                          ? (isDark ? 'bg-white/20 text-white' : 'bg-white text-slate-900 shadow-sm')
                          : (isDark ? 'text-white/50 hover:text-white/80' : 'text-slate-400 hover:text-slate-600')
                      }`} title={th.label} data-testid={`budget-theme-${th.key}`}>
                      <ThIcon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Period Filter */}
          <div className="flex gap-2">
            {['monthly', 'quarterly', 'yearly'].map((p) => (
              <Button key={p}
                variant={period === p ? 'default' : (isDark ? 'secondary' : 'outline')}
                onClick={() => setPeriod(p)}
                className={period !== p && isGlass ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : ''}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>

          {/* Summary Cards */}
          {analytics && (
            <div className="grid md:grid-cols-4 gap-4">
              {summaryCards.map((sc, i) => (
                <Card key={i} className={`p-6 ${card}`}>
                  <p className={`text-sm ${sub} mb-1`}>{sc.label}</p>
                  <p className={`text-3xl font-bold ${sc.color}`}>{sc.value}</p>
                  {isGlass && <div className={`mt-3 h-0.5 rounded-full bg-gradient-to-r ${sc.thermal} opacity-50`} />}
                </Card>
              ))}
            </div>
          )}

          {/* Financial Insights - AI Powered */}
          {financialInsights && financialInsights.ai_powered && (
            <Card className={`p-6 border-l-4 ${
              isGlass ? 'bg-white/5 backdrop-blur-xl border-blue-400/50 border-l-blue-400'
              : isDark ? 'bg-slate-900/80 border-slate-700/50 border-l-blue-600'
              : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-600'
            }`}>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className={`w-6 h-6 ${isGlass ? 'text-blue-400' : 'text-blue-600'}`} />
                <h2 className={`text-2xl font-bold ${txt}`}>AI Financial Insights</h2>
                <span className="ml-auto text-xs bg-blue-600 text-white px-2 py-1 rounded-full">AI Powered</span>
              </div>
              <p className={`${isDark ? 'text-white/80' : 'text-slate-700'} mb-6 font-medium`}>{financialInsights.summary}</p>
              <div className="grid md:grid-cols-2 gap-6">
                {financialInsights.strengths?.length > 0 && (
                  <div>
                    <h3 className={`font-bold ${isGlass ? 'text-emerald-400' : 'text-green-700'} mb-3 flex items-center gap-2`}>
                      <CheckCircle className="w-5 h-5" /> Financial Strengths
                    </h3>
                    <ul className="space-y-2">
                      {financialInsights.strengths.map((s, idx) => (
                        <li key={idx} className={`flex gap-2 text-sm ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                          <span className={`${isGlass ? 'text-emerald-400' : 'text-green-600'} font-bold`}>+</span>{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {financialInsights.concerns?.length > 0 && (
                  <div>
                    <h3 className={`font-bold ${isGlass ? 'text-amber-400' : 'text-orange-700'} mb-3 flex items-center gap-2`}>
                      <AlertTriangle className="w-5 h-5" /> Areas of Concern
                    </h3>
                    <ul className="space-y-2">
                      {financialInsights.concerns.map((c, idx) => (
                        <li key={idx} className={`flex gap-2 text-sm ${isDark ? 'text-white/70' : 'text-slate-700'}`}>
                          <span className={`${isGlass ? 'text-amber-400' : 'text-orange-600'} font-bold`}>!</span>{c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {financialInsights.recommendations?.length > 0 && (
                <div className="mt-6">
                  <h3 className={`font-bold ${isGlass ? 'text-blue-400' : 'text-blue-700'} mb-3 flex items-center gap-2`}>
                    <CheckCircle className="w-5 h-5" /> Actionable Recommendations
                  </h3>
                  <ul className="space-y-2">
                    {financialInsights.recommendations.map((rec, idx) => (
                      <li key={idx} className={`flex gap-2 text-sm p-3 rounded-lg ${
                        isGlass ? 'bg-white/5 text-white/70' : isDark ? 'bg-slate-800/50 text-slate-300' : 'bg-white text-slate-700'
                      }`}>
                        <span className={`${isGlass ? 'text-blue-400' : 'text-blue-600'} font-bold`}>{idx + 1}.</span>{rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}

          {/* Charts */}
          {analytics && analytics.category_breakdown.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6">
              <Card className={`p-6 ${card}`}>
                <h3 className={`text-lg font-bold mb-4 ${txt}`}>Budget by Category</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RePieChart>
                    <Pie data={analytics.category_breakdown} dataKey="budgeted" nameKey="category_name" cx="50%" cy="50%" outerRadius={80} label>
                      {analytics.category_breakdown.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', borderColor: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#fff' : '#0f172a' }} />
                    <Legend wrapperStyle={{ color: chartTickColor }} />
                  </RePieChart>
                </ResponsiveContainer>
              </Card>

              <Card className={`p-6 ${card}`}>
                <h3 className={`text-lg font-bold mb-4 ${txt}`}>Budgeted vs Actual</h3>
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={analytics.category_breakdown} margin={{ bottom: 90, left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis dataKey="category_name" interval={0}
                      tick={(props) => {
                        const { x, y, payload } = props;
                        const words = payload.value.split(' '); const lines = []; let cur = '';
                        words.forEach((w) => { if ((cur + w).length > 12 && cur.length > 0) { lines.push(cur.trim()); cur = w + ' '; } else { cur += w + ' '; } });
                        if (cur.trim()) lines.push(cur.trim());
                        return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => <text key={i} x={0} y={0} dy={i * 12 + 5} textAnchor="middle" fill={chartTickColor} fontSize={10}>{line}</text>)}</g>);
                      }} />
                    <YAxis tick={{ fontSize: 11, fill: chartTickColor }} />
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', borderColor: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#fff' : '#0f172a', fontSize: '12px', borderRadius: '8px' }} labelStyle={{ fontWeight: 'bold' }} />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px', color: chartTickColor }} iconType="rect" />
                    <Bar dataKey="budgeted" fill="#3b82f6" name="Budgeted" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actual" fill="#10b981" name="Actual" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}

          {/* Accounting Sync */}
          {accountingSync && (
            <Card className={`p-6 border-l-4 ${
              isGlass ? 'bg-white/5 backdrop-blur-xl border-emerald-400/50 border-l-emerald-400'
              : isDark ? 'bg-slate-900/80 border-slate-700/50 border-l-emerald-500'
              : 'border-emerald-500'
            }`} data-testid="accounting-sync-widget">
              <div className="flex items-center gap-2 mb-4">
                <ArrowLeftRight className={`w-6 h-6 ${isGlass ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <h2 className={`text-2xl font-bold ${txt}`}>Budget & Accounting Sync</h2>
                <select value={displayCurrency} onChange={e => setDisplayCurrency(e.target.value)}
                  className={`ml-2 h-8 px-2 rounded-md border text-sm font-medium ${
                    isGlass ? 'bg-white/10 border-white/20 text-white' : isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'
                  }`} data-testid="currency-switch">
                  {availableCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className={`ml-auto text-xs ${sub}`}>Synced: {new Date(accountingSync.sync_timestamp).toLocaleTimeString()}</span>
                <Button variant="ghost" size="sm" onClick={fetchAccountingSync}
                  className={isGlass ? 'text-white/60 hover:text-white hover:bg-white/10' : ''}
                  data-testid="refresh-sync-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className={`p-4 rounded-lg ${isGlass ? 'bg-emerald-500/10 border border-emerald-500/20' : isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                  <p className={`text-sm ${sub} mb-1`}>Available Balance (All Banks)</p>
                  <p className={`text-2xl font-bold ${isGlass ? 'text-emerald-400' : 'text-emerald-700'}`}>{currSym}{convertFromUSD(accountingSync.total_available_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div className={`p-4 rounded-lg ${isGlass ? 'bg-blue-500/10 border border-blue-500/20' : isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                  <p className={`text-sm ${sub} mb-1`}>Budget Allocated ({period})</p>
                  <p className={`text-2xl font-bold ${isGlass ? 'text-blue-400' : 'text-blue-700'}`}>{currSym}{convertFromUSD(accountingSync.total_budgeted || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div className={`p-4 rounded-lg ${
                  accountingSync.budget_remaining >= 0
                    ? (isGlass ? 'bg-green-500/10 border border-green-500/20' : isDark ? 'bg-green-900/20' : 'bg-green-50')
                    : (isGlass ? 'bg-red-500/10 border border-red-500/20' : isDark ? 'bg-red-900/20' : 'bg-red-50')
                }`}>
                  <p className={`text-sm ${sub} mb-1`}>Budget Remaining</p>
                  <p className={`text-2xl font-bold ${
                    accountingSync.budget_remaining >= 0
                      ? (isGlass ? 'text-emerald-400' : 'text-green-700')
                      : (isGlass ? 'text-rose-400' : 'text-red-700')
                  }`}>{currSym}{Math.abs(convertFromUSD(accountingSync.budget_remaining || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                  <p className={`text-xs ${sub} mt-1`}>{accountingSync.budget_utilization}% utilized</p>
                </div>
              </div>
              {accountingSync.bank_summary.length > 0 && (
                <div className="mt-4">
                  <h3 className={`font-semibold text-sm ${sub} mb-2`}>Bank Balances</h3>
                  <div className="space-y-2">
                    {accountingSync.bank_summary.map((bank, idx) => (
                      <div key={idx} className={`px-3 py-2 rounded-lg ${
                        isGlass ? 'bg-white/5 border border-white/10' : isDark ? 'bg-slate-800/60' : 'bg-slate-50'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Building2 className={`w-4 h-4 ${isGlass ? 'text-white/40' : 'text-slate-500'}`} />
                            <span className={`font-medium text-sm ${txt}`}>{bank.bank_name}</span>
                          </div>
                          <span className={`text-sm font-bold ${isGlass ? 'text-emerald-400' : 'text-emerald-700'}`}>
                            {currSym}{convertFromUSD(bank.total_balance_usd || bank.total_balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                          </span>
                        </div>
                        {Array.isArray(bank.accounts) && bank.accounts.length > 0 && (
                          <div className="flex gap-3 flex-wrap ml-6">
                            {bank.accounts.filter(a => a.balance !== 0).map((acc, ai) => {
                              const accDisplayBal = displayCurrency === acc.currency
                                ? acc.balance
                                : convertFromUSD(acc.balance_usd || 0);
                              return (
                                <span key={ai} className={`text-xs ${sub}`}>
                                  {acc.account_number} ({acc.currency}): <span className="font-semibold">{currSym}{accDisplayBal.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {accountingSync.recent_transactions.length > 0 && (
                <div className="mt-4">
                  <h3 className={`font-semibold text-sm ${sub} mb-2`}>Recent Expenses</h3>
                  <div className="space-y-1">
                    {accountingSync.recent_transactions.map((t, idx) => (
                      <div key={idx} className={`flex justify-between items-center py-1.5 px-3 rounded text-sm ${
                        isGlass ? 'bg-white/5' : isDark ? 'bg-slate-800/40' : 'bg-slate-50'
                      }`}>
                        <span className={isDark ? 'text-white/70' : 'text-slate-700'}>{t.description || 'Expense'}</span>
                        <div className="flex gap-4 items-center">
                          <span className={`text-xs ${sub}`}>{t.category}</span>
                          <span className={`text-xs ${isGlass ? 'text-white/30' : 'text-slate-400'}`}>{t.date}</span>
                          <span className={`font-semibold ${isGlass ? 'text-rose-400' : 'text-red-600'}`}>-{currSym}{fmtNum(convertFromUSD(t.amount))}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Budget List */}
          <div>
            <h2 className={`text-2xl font-bold mb-4 ${txt}`} data-testid="budget-tracking-title">Budget Tracking</h2>
            <div className="space-y-4">
              {budgets.length === 0 ? (
                <Card className={`p-12 text-center ${card}`}>
                  <Calendar className={`w-16 h-16 mx-auto mb-4 ${isGlass ? 'text-white/20' : 'text-slate-300'}`} />
                  <p className={`${sub} mb-4`}>No budgets set for this period</p>
                  <Button onClick={() => setIsDialogOpen(true)} className={isGlass ? 'bg-emerald-500/80 hover:bg-emerald-500 border-0' : ''}>
                    <Plus className="w-4 h-4 mr-2" /> Create Your First Budget
                  </Button>
                </Card>
              ) : (
                budgets.map((budget, bi) => (
                  <Card key={budget.id} className={`p-6 ${card}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className={`text-xl font-bold ${txt}`}>{budget.category_name}</h3>
                          {budget.alert === 'warning' && <AlertTriangle className={`w-5 h-5 ${isGlass ? 'text-amber-400' : 'text-yellow-600'}`} />}
                          {budget.alert === 'danger' && <AlertTriangle className={`w-5 h-5 ${isGlass ? 'text-rose-400' : 'text-red-600'}`} />}
                          {budget.alert === 'ok' && <CheckCircle className={`w-5 h-5 ${isGlass ? 'text-emerald-400' : 'text-green-600'}`} />}
                        </div>
                        <div className="grid md:grid-cols-4 gap-4 mb-4">
                          <div><p className={`text-sm ${sub}`}>Budgeted</p><p className={`text-lg font-bold ${txt}`}>${fmtNum(budget.amount)}</p></div>
                          <div><p className={`text-sm ${sub}`}>Spent</p><p className={`text-lg font-bold ${txt}`}>${fmtNum(budget.actual_spent || 0)}</p></div>
                          <div><p className={`text-sm ${sub}`}>Remaining</p><p className={`text-lg font-bold ${budget.remaining >= 0 ? (isGlass ? 'text-emerald-400' : 'text-green-600') : (isGlass ? 'text-rose-400' : 'text-red-600')}`}>${fmtNum(Math.abs(budget.remaining || 0))}</p></div>
                          <div><p className={`text-sm ${sub}`}>Used</p><p className={`text-lg font-bold ${txt}`}>{(budget.percentage_used || 0).toFixed(1)}%</p></div>
                        </div>
                        <div className={`w-full ${isGlass ? 'bg-white/10' : isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-full h-3 mb-2`}>
                          <div className={`h-3 rounded-full transition-all ${
                            budget.percentage_used >= 100 ? 'bg-red-600' : budget.percentage_used >= 75 ? 'bg-yellow-600' : 'bg-green-600'
                          }`} style={{ width: `${Math.min(budget.percentage_used || 0, 100)}%` }} />
                        </div>
                        <p className={`text-sm ${sub}`}>Period: {new Date(budget.start_date).toLocaleDateString()} - {new Date(budget.end_date).toLocaleDateString()}</p>
                        {isGlass && <div className={`mt-3 h-0.5 rounded-full bg-gradient-to-r ${THERMAL_GRADIENTS[bi % THERMAL_GRADIENTS.length]} opacity-40`} />}
                      </div>
                      <Button variant={isDark ? 'secondary' : 'outline'} size="sm"
                        className={`${isGlass ? 'text-rose-400 bg-white/10 border-white/20 hover:bg-red-500/20' : 'text-red-600'}`}
                        onClick={() => handleDelete(budget.id)}>Delete</Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* AI Forecast */}
          {forecast && forecast.forecasts && (
            <Card className={`p-6 ${card}`}>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className={`w-6 h-6 ${isGlass ? 'text-blue-400' : 'text-blue-600'}`} />
                <h2 className={`text-2xl font-bold ${txt}`}>AI Forecast - Next Month</h2>
                {forecast.ai_powered && <span className="ml-auto text-xs bg-blue-600 text-white px-2 py-1 rounded-full">AI Powered</span>}
              </div>
              <p className={`${sub} mb-4`}>Predicted total spending: <span className={`font-bold text-lg ${txt}`}>${fmtNum(forecast.total_predicted || 0)}</span></p>
              {forecast.overall_insight && (
                <p className={`mb-6 p-4 rounded-lg border-l-4 font-medium ${
                  isGlass ? 'text-white/80 bg-blue-500/10 border-blue-400/50'
                  : isDark ? 'text-slate-300 bg-blue-900/20 border-blue-600'
                  : 'text-slate-700 bg-blue-50 border-blue-600'
                }`}>{forecast.overall_insight}</p>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {forecast.forecasts.map((f) => (
                  <Card key={f.category_id} className={`p-4 border-l-4 ${card}`}
                    style={{ borderLeftColor: f.trend === 'increasing' ? '#ef4444' : f.trend === 'decreasing' ? '#10b981' : '#94a3b8' }}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className={`font-bold ${txt}`}>{f.category_name}</h4>
                      {f.trend === 'increasing' && <TrendingUp className={`w-4 h-4 ${isGlass ? 'text-rose-400' : 'text-red-600'}`} />}
                      {f.trend === 'decreasing' && <TrendingDown className={`w-4 h-4 ${isGlass ? 'text-emerald-400' : 'text-green-600'}`} />}
                    </div>
                    <p className={`text-2xl font-bold ${isGlass ? 'text-blue-400' : 'text-blue-600'} mb-1`}>${fmtNum(f.predicted_amount || 0)}</p>
                    <p className={`text-sm ${sub} mb-2`}>{f.confidence} confidence - {f.trend}</p>
                    {f.insight && <p className={`text-xs p-2 rounded mt-2 ${isGlass ? 'text-white/60 bg-white/5' : isDark ? 'text-slate-400 bg-slate-800/50' : 'text-slate-600 bg-slate-50'}`}>{f.insight}</p>}
                    {f.average_monthly !== undefined && <p className={`text-xs ${sub} mt-1`}>Avg: ${fmtNum(f.average_monthly || 0)} ({f.data_points || 0} months)</p>}
                  </Card>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
